import { LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';
import { useMemo } from 'react';

import {
  GeneralSurveyFragment,
  GeneralSurveyQuery,
  GeneralSurveyQueryVariables,
  Survey_Type,
  useGeneralSurveyQuery as useApolloGeneralSurveyQuery,
  useGeneralSurveysQuery,
  GeneralSurveysQuery,
  GeneralSurveysQueryVariables,
  useGeneralSurveysLazyQuery,
  useSurveyQuestionResponseStatisticsQuery,
  useSurveyLocationStatisticsQuery,
  SurveyQuestionResponseStatisticsQueryVariables,
  SurveyQuestionResponseStatisticsQuery,
  SurveyMetricsQuery,
  SurveyMetricsQueryVariables,
  useSurveyMetricsQuery,
} from '@occc/graphql';
import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { GetGeneralSurveysProps, UseSurveyLocationStatisticsParams } from './types';

import { useLazyQuery, useQuery } from '../utils';

export const useSurveyLocationStatistics = ({
  baseOptions,
  paginationInfo,
  onPageChange,
}: UseSurveyLocationStatisticsParams = {}) => {
  const locationStatisticsResponse = usePaginatedQuery(
    useSurveyLocationStatisticsQuery,
    'surveyLocationStatistics.pageInfo',
    undefined,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  const data = useMemo(() => {
    return locationStatisticsResponse.data?.surveyLocationStatistics.edges.map((e) => e.node);
  }, [locationStatisticsResponse.data?.surveyLocationStatistics.edges]);

  const response = useMemo(() => {
    return {
      ...locationStatisticsResponse,
      data,
    };
  }, [locationStatisticsResponse, data]);

  return response;
};

export const useGetGeneralSurveys = ({
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetGeneralSurveysProps = {}) => {
  const response = usePaginatedQuery(
    useGeneralSurveysQuery,
    'surveys.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
      variables: {
        ...baseOptions?.variables,
        filter: {
          ...baseOptions?.variables?.filter,
          typeEquals: Survey_Type.General,
        },
      },
    },
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    // It is always GeneralSurveyFragment because query always has a filter typeEquals: Survey_Type.General
    data: response.data?.surveys.edges.map((e) => e.node) as GeneralSurveyFragment[],
  };
};

export const useGetGeneralSurveysLazy = (
  baseOptions?: LazyQueryHookOptions<GeneralSurveysQuery, GeneralSurveysQueryVariables>,
) => {
  const [executeQuery, response] = useLazyQuery<GeneralSurveysQuery, GeneralSurveysQueryVariables>(
    useGeneralSurveysLazyQuery,
    baseOptions,
  );

  return {
    executeQuery,
    response: {
      ...response,
      // it's a GeneralSurveyFragment because query always has a filter "typeEquals: Survey_Type.General"
      data: response.data?.surveys.edges.map((e) => e.node) as GeneralSurveyFragment[],
    },
  };
};

export const useGetGeneralSurveyById = (
  baseOptions?: QueryHookOptions<GeneralSurveyQuery, GeneralSurveyQueryVariables>,
) => {
  const { data, ...rest } = useQuery(useApolloGeneralSurveyQuery, {
    ...baseOptions,
    skip: !baseOptions?.variables?.uuid,
  });

  if (data?.survey.__typename !== 'GeneralSurvey') {
    return { survey: undefined, ...rest };
  }

  return {
    survey: data.survey,
    ...rest,
  };
};

export const useGetSurveyQuestionResponseStatistics = (
  baseOptions?: QueryHookOptions<
    SurveyQuestionResponseStatisticsQuery,
    SurveyQuestionResponseStatisticsQueryVariables
  >,
) => {
  const { data, ...rest } = useQuery(useSurveyQuestionResponseStatisticsQuery, {
    ...baseOptions,
    skip: !baseOptions?.variables?.questionUuid,
  });

  return {
    statistics: data?.surveyQuestionResponseStatistics || null,
    ...rest,
  };
};

export const useGetSurveyMetrics = (
  baseOptions?: QueryHookOptions<SurveyMetricsQuery, SurveyMetricsQueryVariables>,
) => {
  const response = useQuery(useSurveyMetricsQuery, baseOptions);
  return response;
};
